import React, { useMemo, useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { createEditor, Transforms, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import './TagTextField.css';
import { resolveTagName } from '../utils/tagDisplay';

const createEmptyParagraph = () => [{ type: 'paragraph', children: [{ text: '' }] }];

// Compact, human label for a tag pill: the formatting it represents (B/I/U)
// rather than the opaque numeric id. Closing tags are prefixed with "/".
const TAG_LETTER = { b: 'B', strong: 'B', i: 'I', em: 'I', u: 'U' };
const tagPillLabel = (tagMap, tag, open) => {
    if (tag === 'br') return '↵';
    const name = resolveTagName(tagMap, tag);
    const base = (name && (TAG_LETTER[name.toLowerCase()] || name)) || tag;
    return open ? base : `/${base}`;
};

const normalizeSlateValue = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
        return createEmptyParagraph();
    }
    return value;
};

const TagTextField = forwardRef(function TagTextField({
    value = '',
    onChange,
    dir = 'ltr',
    disabled = false,
    style = {},
    tagMap = {},
    onKeyDown,
    placeholder,
    className = '',
    inlineBreaks = false
}, ref) {
    const editor = useMemo(() => {
        const e = withReact(createEditor());
        const { isInline, isVoid } = e;
        e.isInline = element => (element.type === 'tag' ? true : isInline(element));
        e.isVoid = element => (element.type === 'tag' ? true : isVoid(element));
        return e;
    }, []);

    // Imperative API for the host editor: focus, and insert a tag pill at the
    // caret (used by the tag palette's click-to-insert).
    useImperativeHandle(ref, () => ({
        focus: () => { try { ReactEditor.focus(editor); } catch { /* not mounted */ } },
        insertTag: (tag, open, opts = {}) => {
            try { ReactEditor.focus(editor); } catch { /* not mounted */ }
            if (!editor.selection) {
                Transforms.select(editor, Editor.end(editor, []));
            }
            const node = { type: 'tag', tag: String(tag), open: !!open, children: [{ text: '' }] };
            if (String(tag) === 'br') node.nl = !!opts.nl;
            Transforms.insertNodes(editor, node);
        },
    }), [editor]);

    // Parse/normalize the incoming value to a valid Slate document
    const [val, setVal] = useState(() => normalizeSlateValue(stringToSlate(value, inlineBreaks)));

    useEffect(() => {
        const slateVal = normalizeSlateValue(stringToSlate(value, inlineBreaks));
        setVal(slateVal);
    }, [value, inlineBreaks]);

    // Use a stable key so Slate remounts cleanly when the doc shape changes
    const valKey = useMemo(() => {
        try { return JSON.stringify(val); } catch { return 'doc'; }
    }, [val]);

    const renderElement = useCallback(({ attributes, children, element }) => {
        if (element.type === 'tag') {
            const { tag, open } = element;
            const isBreak = tag === 'br';
            const tagName = isBreak ? 'line break' : resolveTagName(tagMap, tag);
            const type = isBreak ? 'break' : (open ? 'open' : 'close');
            const typeLabel = open ? 'opening' : 'closing';
            const capitalizedTypeLabel = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
            const ariaLabel = isBreak
                ? 'line break'
                : (tagName ? `${tagName} ${typeLabel} tag ${tag}` : `${typeLabel} tag ${tag}`);
            const titleText = isBreak
                ? 'Line break'
                : (tagName ? `${tagName} (${typeLabel} ${tag})` : `${capitalizedTypeLabel} tag ${tag}`);

            return (
                <span
                    {...attributes}
                    data-tag={tag}
                    data-type={type}
                    data-tag-name={tagName || ''}
                    title={titleText}
                    aria-label={ariaLabel}
                    contentEditable={false}
                    draggable
                    onDragStart={e => {
                        e.dataTransfer.setData('text/tag', tag);
                        e.dataTransfer.setData('text/type', type);
                        if (isBreak) e.dataTransfer.setData('text/nl', element.nl ? '1' : '0');
                    }}
                    className="inline-tag"
                >
                    <span className="tag-pill" aria-hidden="true">{tagPillLabel(tagMap, tag, open)}</span>
                </span>
            );
        }
        return <p {...attributes}>{children}</p>;
    }, [tagMap]);

    const handleDrop = useCallback(
        e => {
            if (!e.dataTransfer.types.includes('text/tag')) return;
            e.preventDefault();
            const tag = e.dataTransfer.getData('text/tag');
            const type = e.dataTransfer.getData('text/type');
            if (!tag) return;
            const node = { type: 'tag', tag, open: type === 'open', children: [{ text: '' }] };
            if (tag === 'br') node.nl = e.dataTransfer.getData('text/nl') === '1';
            Transforms.insertNodes(editor, node);
        },
        [editor]
    );

    const onSlateChange = useCallback(
        v => {
            const nextValue = normalizeSlateValue(v);
            setVal(nextValue);
            if (onChange) onChange(slateToString(nextValue));
        },
        [onChange]
    );

    return (
        <div dir={dir} style={style} className={`tag-text-field ${className}`.trim()}>
            {/* ✅ Use initialValue, not value */}
            <Slate key={valKey} editor={editor} initialValue={val} onChange={onSlateChange}>
                <Editable
                    renderElement={renderElement}
                    readOnly={disabled}
                    spellCheck={false}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    onDrop={handleDrop}
                    onDragOver={e => {
                        if (e.dataTransfer.types.includes('text/tag')) e.preventDefault();
                    }}
                />
            </Slate>
        </div>
    );
});

export default TagTextField;

function stringToSlate(text, inlineBreaks = false) {
    if (typeof text !== 'string') return createEmptyParagraph();
    text = text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

    // inlineBreaks: keep everything in ONE paragraph and turn \n into an inline
    // break pill (round-tripped back to \n). Otherwise split on \n into
    // separate paragraphs (legacy behaviour).
    const createChildrenFromText = segText => {
        const regex = inlineBreaks ? /<\/?\d+>|<br\s*\/?>|\n/gi : /<\/?\d+>|<br\s*\/?>/gi;
        let last = 0;
        const children = [];
        let match;
        while ((match = regex.exec(segText)) !== null) {
            if (match.index > last) {
                children.push({ text: segText.slice(last, match.index) });
            }
            const tagText = match[0];
            // "\n" or "<br/>" / "<br>" → tag 'br' (nl marks newline origin);
            // "<1>" / "</1>" → numeric id.
            const isNewline = tagText === '\n';
            const isBreak = isNewline || /^<br/i.test(tagText);
            const node = isBreak
                ? { type: 'tag', tag: 'br', open: true, nl: isNewline, children: [{ text: '' }] }
                : { type: 'tag', tag: tagText.replace(/[<>/]/g, ''), open: !tagText.startsWith('</'), children: [{ text: '' }] };
            children.push(node);
            last = match.index + tagText.length;
        }
        if (last < segText.length) {
            children.push({ text: segText.slice(last) });
        }
        if (children.length === 0) children.push({ text: '' });
        return children;
    };

    const segments = inlineBreaks ? [text] : text.split('\n');
    const nodes = segments.map(segText => ({
        type: 'paragraph',
        children: createChildrenFromText(segText),
    }));

    if (nodes.length === 0) {
        return createEmptyParagraph();
    }

    return nodes;
}

function slateToString(value) {
    const nodeToString = node => {
        if (node.type === 'tag') {
            if (node.tag === 'br') return node.nl ? '\n' : '<br/>';
            return node.open ? `<${node.tag}>` : `</${node.tag}>`;
        }
        if (node.text) return node.text;
        return (node.children || []).map(n => nodeToString(n)).join('');
    };

    if (!Array.isArray(value)) {
        return '';
    }

    const paragraphs = value.map(node => {
        if (node.type === 'paragraph') {
            return (node.children || []).map(child => nodeToString(child)).join('');
        }
        return nodeToString(node);
    });

    return paragraphs.join('\n');
}

export { stringToSlate, slateToString };
